<template>
  <div v-if="links.length">
    <v-btn
      v-for="link in links"
      :key="link.label"
      :to="link.to"
      x-small
      outlined
      color="primary"
      class="mr-2"
    >
      {{ link.label }}
    </v-btn>
  </div>
</template>

<script>
/**
 * Mode-aware navigation from a query-photos card to the page where this photo
 * is actually worked: the tagger queue and/or the QC queue for the tenant's
 * (captureMode × taggingMode). Each link carries the photo's identifiers as
 * query params, which the target view seeds into its filter before the initial
 * fetch (routeSeed.js) — so the user lands already scoped to this photo
 * instead of re-entering ids in the filter modal.
 */
import {mapGetters} from 'vuex'

export default {
  name: 'PhotoQuickLinks',
  props: {
    image: {type: Object, required: true},
  },
  computed: {
    ...mapGetters(['isGroupMode', 'isCountMode', 'isBboxMode', 'taggerHomeRoute', 'qcHomeRoute']),
    roles() {
      const profile = this.$store.state.auth.profile || {}
      return (profile.user && profile.user.roles) || []
    },
    // Group pages (and bbox, which is group-keyed) address work by the
    // (response_group_id, question_id) group key; photo pages by response id.
    linkQuery() {
      if (this.isGroupMode || this.isBboxMode) {
        const query = {rgid: String(this.image.response_group_id)}
        // NULL on count-mode / un-grouped tenants; rgid alone still matches.
        if (this.image.question_id != null) {
          query.questionId = String(this.image.question_id)
        }
        return query
      }
      return {respId: String(this.image.response_id)}
    },
    // Whether the tagger queue already considers this done, so a "continue
    // tagging" link would dead-end on an empty queue. Group/bbox queues check
    // out whole groups and track tagged on the PhotoGroup; a group-mode photo's
    // own `tagged` stays False even after its group is finished, so in those
    // modes we must read the group's flag (`group_tagged`, supplied by
    // serialize_photo_list_raw) rather than the per-photo one.
    alreadyTagged() {
      if (this.isGroupMode || this.isBboxMode) {
        // null => no group row resolved (shouldn't happen in group/bbox mode);
        // treat as "not done" so the link still shows rather than silently
        // disappearing.
        return this.image.group_tagged === true
      }
      return this.image.tagged === true
    },
    links() {
      const links = []

      // Tagging queues serve not-yet-done work only (get_groups hardcodes
      // untagged_groups(); filter_photos defaults onlyUntagged), so the link
      // would land an already-tagged photo/group on an empty queue.
      if (this.roles.includes('Tagger') && !this.alreadyTagged) {
        const label = this.isBboxMode ? 'Boxes' : (this.isCountMode ? 'Count' : 'Tag')
        links.push({label, to: {path: this.taggerHomeRoute, query: this.linkQuery}})
      }

      if (this.roles.includes('Quality Control')) {
        // qcHomeRoute still points bbox tenants at the tagger canvas; QC wants
        // the review-mode route added by IT-745.
        const qcPath = this.isBboxMode ? '/box-qc' : this.qcHomeRoute
        // Group QC pins reviewed=False by default; showQCed widens the queue
        // so an already-reviewed group still comes up. Photo QC lifts its pins
        // on a respId search by itself, so its link stays clean.
        const query = (this.isGroupMode || this.isBboxMode)
          ? {...this.linkQuery, showQCed: '1'}
          : this.linkQuery
        links.push({label: 'QC', to: {path: qcPath, query}})
      }

      return links
    },
  },
}
</script>
